import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FundService } from 'src/app/services/fund/fund.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ToastService } from 'src/app/services/toast/toast.service';
import { FundOperationalStatusService } from 'src/app/services/fund-operational-status/fund-operational-status.service';
import {
  FundOperationalModalMode,
  FundOperationalPrefill,
} from 'src/app/components/fund-operational-status-modal/fund-operational-status-modal.component';
import {
  getFundTransferTags,
  getInactivationReasonLabel as resolveInactivationReasonLabel,
  hasAwaitingConfirmationTransferOut,
  hasPendingTransferOut,
  isFundOperationallyActive as isFundActive,
  mapFundInactivationFromFund,
  mapPendingTransferOutPrefill,
} from 'src/app/helpers/fund-operational-status';

@Component({
  selector: 'app-dashboard-organization',
  templateUrl: './dashboard-organization.component.html',
  styleUrls: ['./dashboard-organization.component.scss'],
})
export class DashboardOrganizationComponent implements OnInit {
  @ViewChild('form') form: ElementRef<HTMLDivElement> | undefined;

  permissionObj: PermissionValues = {
    canAdd: false,
    canChange: false,
    canFilter: false,
  };

  loading = true;

  today = Utils.todayString();

  funds: any[] = [];
  count: number = 0;
  offset: number = 0;

  anbimaChoices: SelectItem[] = [];

  statusChoices: SelectItem[] = [
    { label: 'Ativo', value: 'fund_status:active' },
    { label: 'Inativo', value: 'fund_status:inactive' },
    { label: 'Em aprovação', value: 'status:pending' },
    { label: 'Aprovado', value: 'status:approved' },
    { label: 'Homologado', value: 'status:homologated' },
    { label: 'Rejeitado', value: 'status:rejected' },
  ];

  transferTypeChoices: SelectItem[] = [
    { label: 'Transferência agendada', value: 'transfer_status:pending' },
    {
      label: 'Transferência a confirmar',
      value: 'transfer_status:awaiting_confirmation',
    },
    { label: 'Concluída', value: 'transfer_status:completed' },
    { label: 'Recebido', value: 'transfer_type:transfer-in' },
    { label: 'Transferido', value: 'transfer_type:transfer-out' },
  ];

  applicableRole = 'ADM';
  results_per_page = 10;
  summary: any = null;

  filtered = false;
  dataFilters: any = null;

  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];

  selectItems: SelectItem[] = [
    { label: '20 por página', value: '2' },
    { label: '30 por página', value: '3' },
    { label: '50 por página', value: '5' },
  ];

  perPageForm = this.formBuilder.group({
    perPage: ['20', [Validators.required]],
  });

  filterForm = this.formBuilder.group({
    search: [null],
    fund_type: [null],
    status_option: [null],
    transfer_option: [null],
  });

  showFilterModal = false;

  fundOperationalModalMode: FundOperationalModalMode = null;
  fundOperationalPrefill: FundOperationalPrefill | null = null;
  selectedFundApproval: any = null;

  // Carbon Warning Modal properties
  showDeleteModal = false;
  showSuccessModal = false;
  showErrorModal = false;
  fundToDelete: any = null;
  showCancelScheduleModal = false;
  showConfirmInactivationModal = false;
  scheduleActionLoading = false;
  actionsDropdownFundId: string | number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    public signupService: SignupService,
    private authService: AuthService,
    private fundService: FundService,
    private storageService: StorageService,
    private toast: ToastService,
    private fundOperationalStatusService: FundOperationalStatusService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_fund']);

    if (this.authService.user.active_register) {
      const role =
        this.authService.user.active_register.register.role.slug.split('-');
      this.applicableRole = role[0];
    } else {
      this.applicableRole = 'ADM';
    }

    this.getData();
    this.setPermissions();
  }

  getStatus(item: UserApprovalResponse) {
    if (item.completed) {
      if (item.is_approved) {
        return 'Aprovado internamente';
      } else {
        return 'Fundo rejeitado';
      }
    } else {
      if (item.current_department !== null) {
        return item.current_department.type === 'department'
          ? item.current_department.department.name
          : item.current_department.agent.name;
      } else if (item.step === 0) {
        return 'Edição';
      } else {
        return 'Departamento não definido';
      }
    }
  }

  getStatusForBadge(item: UserApprovalResponse): string {
    if (item.completed) {
      if (item.is_approved) {
        return 'Homologado';
      } else {
        return 'Rejeitado';
      }
    } else {
      return 'Em aprovação';
    }
  }

  getStatusBadgeClass(item: UserApprovalResponse): string {
    const status = this.getStatusForBadge(item);

    if (status === 'Homologado') {
      return 'status-badge-homologated';
    } else if (status === 'Rejeitado') {
      return 'status-badge-rejected';
    } else {
      // Outros status (Em aprovação, etc) = azul
      return 'status-badge-approval';
    }
  }

  getStatusInfo(item: UserApprovalResponse) {
    if (item.completed) {
      if (item.is_approved) {
        return 'Integração ao fundo';
      } else {
        if (item.current_department !== null) {
          return item.current_department.type === 'department'
            ? item.current_department.department.name
            : item.current_department.agent.name;
        } else if (item.step === 0) {
          return 'Edição';
        } else {
          return 'Departamento não definido';
        }
      }
    } else if (item.step === 0) {
      return '';
    } else {
      return `Aguardando aprovação (${item.step})`;
    }
  }

  getFundInitials(approval: any) {
    return approval.fund.name
      .toUpperCase()
      .replace(/[^\w\s]/gi, '')
      .split(' ')
      .slice(0, 2)
      .map((word: string) => word.charAt(0))
      .join('');
  }

  formatDocument(document: any) {
    if (!document) return '-';
    return document.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }

  getAssignorsCount(approval: any): string {
    // Verificar se há dados de cedentes no objeto fund
    if (approval.fund?.cedentes_count !== undefined) {
      return String(approval.fund.cedentes_count);
    }
    // Fallback para outras estruturas possíveis
    if (approval.fund?.assignors_count !== undefined) {
      return String(approval.fund.assignors_count);
    }
    if (approval.assignors_count !== undefined) {
      return String(approval.assignors_count);
    }
    if (approval.fund?.assignors?.length !== undefined) {
      return String(approval.fund.assignors.length);
    }
    if (approval.assignors?.length !== undefined) {
      return String(approval.assignors.length);
    }
    return '0';
  }

  getShareholdersCount(approval: any): string {
    // Verificar se há dados de cotistas no objeto fund
    if (approval.fund?.cotistas_count !== undefined) {
      return String(approval.fund.cotistas_count);
    }
    // Fallback para outras estruturas possíveis
    if (approval.fund?.shareholders_count !== undefined) {
      return String(approval.fund.shareholders_count);
    }
    if (approval.shareholders_count !== undefined) {
      return String(approval.shareholders_count);
    }
    if (approval.fund?.shareholders?.length !== undefined) {
      return String(approval.fund.shareholders.length);
    }
    if (approval.shareholders?.length !== undefined) {
      return String(approval.shareholders.length);
    }
    return '0';
  }

  getFundTransferTags(approval: any): string[] {
    return getFundTransferTags(approval?.fund);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = []; // ✅ Limpar tags
    this.filterForm.reset();
    this.dataFilters = null;
    this.getFunds();
    this.showFilterModal = false;
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      search: 'Nome / CNPJ',
      fund_type: 'Classificação ANBIMA',
      status_option: 'Status',
      transfer_option: 'Tipo de transferência',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para arrays (múltipla seleção)
        if (Array.isArray(value) && value.length > 0) {
          if (key === 'fund_type') {
            displayValue = value
              .map((id: number) => {
                const choice = this.anbimaChoices.find((c) => c.value === id);
                return choice ? choice.label : id;
              })
              .join(', ');
          } else {
            displayValue = value.join(', ');
          }
        } else if (key === 'status_option') {
          displayValue =
            this.statusChoices.find((choice) => choice.value === value)
              ?.label ?? value;
        } else if (key === 'transfer_option') {
          displayValue =
            this.transferTypeChoices.find((choice) => choice.value === value)
              ?.label ?? value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  private applyEncodedFilter(
    filters: Record<string, any>,
    encoded?: string | null
  ): void {
    if (!encoded || typeof encoded !== 'string') return;

    const separatorIndex = encoded.indexOf(':');
    if (separatorIndex <= 0) return;

    const param = encoded.slice(0, separatorIndex);
    const value = encoded.slice(separatorIndex + 1);
    if (param && value) {
      filters[param] = value;
    }
  }

  private buildApiFiltersFromForm(values: any): Record<string, any> {
    const filters: Record<string, any> = {};

    if (values?.search != null && values.search !== '') {
      filters.search = values.search;
    }

    if (
      values?.fund_type != null &&
      values.fund_type !== '' &&
      !(Array.isArray(values.fund_type) && values.fund_type.length === 0)
    ) {
      filters.fund_type = values.fund_type;
    }

    this.applyEncodedFilter(filters, values?.status_option);
    this.applyEncodedFilter(filters, values?.transfer_option);

    return filters;
  }

  private restoreFormFiltersFromApi(dataFilters: any): Record<string, any> {
    const filtersToRestore: Record<string, any> = {};

    if (dataFilters?.search != null && dataFilters.search !== '') {
      filtersToRestore.search = dataFilters.search;
    }

    if (dataFilters?.fund_type != null && dataFilters.fund_type !== '') {
      filtersToRestore.fund_type = dataFilters.fund_type;
    }

    if (dataFilters?.fund_status) {
      filtersToRestore.status_option = `fund_status:${dataFilters.fund_status}`;
    } else if (dataFilters?.status) {
      filtersToRestore.status_option = `status:${dataFilters.status}`;
    }

    if (dataFilters?.transfer_status) {
      filtersToRestore.transfer_option = `transfer_status:${dataFilters.transfer_status}`;
    } else if (dataFilters?.transfer_type) {
      filtersToRestore.transfer_option = `transfer_type:${dataFilters.transfer_type}`;
    }

    return filtersToRestore;
  }

  async filterData() {
    this.loading = true;
    try {
      const values = this.filterForm.value;
      const filters = this.buildApiFiltersFromForm(values);

      const { total_items, results, results_per_page } = await this.api.get<
        ApiResponse<UserApprovalResponse[]>
      >({
        route: 'api/v2/approvals/funds-optimized/',
        token: true,
        params: {
          page: 1,
          ...filters,
        },
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.dataFilters = Object.keys(filters).length > 0 ? filters : null;

      this.count = total_items;
      this.offset = results.length;
      this.results_per_page = results_per_page;

      this.funds = results;
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de cedente.'
      );
      // Em caso de erro, não atualiza hasFilters
    }
  }

  async getData() {
    this.loading = true;
    const filters: any = {};

    // Construir filtros a partir do formulário (se houver dataFilters, usar eles)
    if (this.dataFilters) {
      Object.entries(this.dataFilters).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          filters[key] = value;
        }
      });
    } else {
      Object.assign(filters, this.buildApiFiltersFromForm(this.filterForm.value));
    }

    await Promise.all([this.getChoices()]);

    // ✅ Aplicar filtros se houver
    if (Object.keys(filters).length > 0) {
      this.dataFilters = filters;
    }

    // ✅ Fazer requisição com filtros aplicados (sempre faz a requisição)
    await this.getFunds(1);
  }

  async getChoices() {
    try {
      const res = await this.api.get({
        route: 'api/registration/new/fund-choices',
        token: true,
      });

      this.anbimaChoices = res.ANBIMA_CLASSIFICATION_CHOICES.map(
        (choice: any) => {
          return {
            label: choice.label,
            value: choice.id,
          };
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  async getFunds(page: number = 1) {
    try {
      const filters: any = {};
      if (this.dataFilters) {
        Object.entries(this.dataFilters).forEach(([key, value]: any) => {
          if (value !== null && value !== '') {
            filters[key] = value;
          }
        });
      } else {
        Object.assign(
          filters,
          this.buildApiFiltersFromForm(this.filterForm.value)
        );
      }

      const params = { ...filters, page };

      const { total_items, results, results_per_page, summary } = await this.api.get({
        route: `api/v2/approvals/funds-optimized/`,
        params,
        token: true,
      });

      //   const resTeste = await this.api.get({
      //     route: `api/registration/new/fund/`,
      //     token: true,
      //   });

      this.count = total_items;
      this.offset = (page - 1) * results_per_page + results.length;
      this.results_per_page = results_per_page;
      this.summary = summary;

      this.funds = results;

      const funds = results.map((approval: any) => {
        return {
          label: approval.fund.name,
          value: approval.fund.id,
        };
      });

      this.fundService.fundList = funds;
      this.fundService.onChange.emit();
      this.loading = false;
    } catch (error) {
      console.error(error);
    }
  }

  openDeleteModal(fund: any) {
    this.fundToDelete = fund;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.fundToDelete = null;
  }

  async confirmDeleteFund() {
    if (!this.fundToDelete) return;

    try {
      await this.api.delete({
        route: 'api/fund',
        token: true,
        params: {
          id: this.fundToDelete.fund.id,
        },
      });

      this.funds = this.funds.filter(
        (approval) => approval.fund.id !== this.fundToDelete.fund.id
      );

      this.closeDeleteModal();
      this.showSuccessModal = true;
      this.getFunds();
    } catch (error) {
      this.closeDeleteModal();
      this.showErrorModal = true;
    }
  }

  closeSuccessModal() {
    this.showSuccessModal = false;
  }

  closeErrorModal() {
    this.showErrorModal = false;
  }

  // Modal methods
  getDeleteModalTitle() {
    return 'Tem certeza?';
  }

  getDeleteModalMessage() {
    return 'Você não poderá reverter isso!';
  }

  getDeleteConfirmText() {
    return 'Sim, apague!';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  getSuccessModalTitle() {
    return 'Apagado!';
  }

  getSuccessModalMessage() {
    return 'Fundo apagado com sucesso';
  }

  getSuccessConfirmText() {
    return 'OK';
  }

  getErrorModalTitle() {
    return 'Erro!';
  }

  getErrorModalMessage() {
    return 'Erro ao apagar fundo';
  }

  getErrorConfirmText() {
    return 'OK';
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados (dataFilters), restaurar no formulário
      if (this.dataFilters && Object.keys(this.dataFilters).length > 0) {
        const filtersToRestore = this.restoreFormFiltersFromApi(
          this.dataFilters
        );
        if (Object.keys(filtersToRestore).length > 0) {
          this.filterForm.patchValue(filtersToRestore);
        }
        this.hasFilters = true;
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  closeModal() {
    this.showFilterModal = false;
  }

  changePageData(page: number) {
    this.loading = true;
    this.getFunds(page);
  }

  redirectToNewFund() {
    this.router.navigate(['/app/funds/new']);
  }

  refreshData() {
    this.loading = true;
    this.getData();
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_fund`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  /**
   * Verifica se há algum campo do formulário de filtros preenchido
   */
  hasFilterValues(): boolean {
    const values = this.filterForm.value;

    // Verifica cada campo do formulário
    for (const [key, value] of Object.entries(values)) {
      if (value !== null && value !== '' && value !== undefined) {
        // Se for array, verifica se tem pelo menos um item
        if (Array.isArray(value) && value.length > 0) {
          return true;
        }
        // Se não for array e tiver valor, retorna true
        if (!Array.isArray(value)) {
          return true;
        }
      }
    }

    return false;
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    try {
      // Se já vem no formato "DD/MM/YYYY HH:MM"
      if (dateStr.includes('/') && dateStr.includes(' ')) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');

        return {
          date: `${day}/${month}/${year}`,
          time: `às ${hours}:${minutes}`,
        };
      }

      // Se for ISO datetime
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${day}/${month}/${year}`,
        time: `às ${hours}:${minutes}`,
      };
    } catch (error) {
      return { date: dateStr || '-', time: '' };
    }
  }

  isFundOperationallyActive(approval: any): boolean {
    return isFundActive(approval?.fund);
  }

  hasScheduledTransfer(approval: any): boolean {
    return hasPendingTransferOut(approval?.fund);
  }

  hasAwaitingConfirmation(approval: any): boolean {
    return hasAwaitingConfirmationTransferOut(approval?.fund);
  }

  canInactivateFund(approval: any): boolean {
    return (
      this.isFundOperationallyActive(approval) &&
      !this.hasScheduledTransfer(approval) &&
      !this.hasAwaitingConfirmation(approval)
    );
  }

  canReactivateFund(approval: any): boolean {
    return !this.isFundOperationallyActive(approval);
  }

  canEditScheduledTransfer(approval: any): boolean {
    return (
      this.hasScheduledTransfer(approval) ||
      this.hasAwaitingConfirmation(approval)
    );
  }

  canCancelScheduledTransfer(approval: any): boolean {
    return this.canEditScheduledTransfer(approval);
  }

  canConfirmInactivation(approval: any): boolean {
    return this.hasAwaitingConfirmation(approval);
  }

  hasFundOperationalActions(approval: any): boolean {
    if (!this.permissionObj.canChange) return false;

    return (
      this.canEditScheduledTransfer(approval) ||
      this.canCancelScheduledTransfer(approval) ||
      this.canConfirmInactivation(approval) ||
      this.canInactivateFund(approval) ||
      this.canReactivateFund(approval)
    );
  }

  getFundRowId(approval: any): string | number | null {
    return approval?.fund?.id ?? approval?.id ?? null;
  }

  isFundActionsOpen(approval: any): boolean {
    const id = this.getFundRowId(approval);
    return id != null && this.actionsDropdownFundId === id;
  }

  toggleFundActions(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const id = this.getFundRowId(approval);
    this.actionsDropdownFundId = this.actionsDropdownFundId === id ? null : id;
  }

  closeFundActions(): void {
    this.actionsDropdownFundId = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeFundActions();
  }

  viewFund(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    const fundId = approval?.fund?.id;
    if (!fundId) return;
    this.router.navigate(['/app/funds/approval/', fundId]);
  }

  getFundInactiveReasonTooltip(approval: any): string {
    const inactivation = mapFundInactivationFromFund(approval?.fund);
    if (!inactivation) return 'Inativo';

    if (inactivation.reason === 'other') {
      return inactivation.comment || inactivation.reasonLabel || resolveInactivationReasonLabel('other');
    }

    return inactivation.reasonLabel || resolveInactivationReasonLabel(inactivation.reason);
  }

  openFundInactivateModal(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    this.selectedFundApproval = approval;
    this.fundOperationalPrefill = null;
    this.fundOperationalModalMode = 'inactivate';
  }

  openFundEditScheduleModal(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    this.selectedFundApproval = approval;
    this.fundOperationalPrefill = mapPendingTransferOutPrefill(approval?.fund);
    this.fundOperationalModalMode = 'inactivate';
  }

  openFundActivateModal(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    this.selectedFundApproval = approval;
    this.fundOperationalPrefill = null;
    this.fundOperationalModalMode = 'activate';
  }

  openCancelScheduleModal(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    this.selectedFundApproval = approval;
    this.showCancelScheduleModal = true;
  }

  closeCancelScheduleModal(): void {
    if (this.scheduleActionLoading) return;
    this.showCancelScheduleModal = false;
  }

  openConfirmInactivationModal(approval: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeFundActions();
    this.selectedFundApproval = approval;
    this.showConfirmInactivationModal = true;
  }

  closeConfirmInactivationModal(): void {
    if (this.scheduleActionLoading) return;
    this.showConfirmInactivationModal = false;
  }

  async confirmCancelSchedule(): Promise<void> {
    const fundId = this.selectedFundApproval?.fund?.id;
    if (!fundId) return;

    this.scheduleActionLoading = true;
    try {
      await this.fundOperationalStatusService.cancelScheduledInactivation(fundId);
      this.showCancelScheduleModal = false;
      this.toast.show('info', 'Sucesso', 'Agendamento cancelado com sucesso.');
      this.onFundOperationalStatusChanged();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.detail || 'Não foi possível cancelar o agendamento.'
      );
    } finally {
      this.scheduleActionLoading = false;
    }
  }

  async confirmScheduledInactivationFromList(): Promise<void> {
    const fundId = this.selectedFundApproval?.fund?.id;
    if (!fundId) return;

    this.scheduleActionLoading = true;
    try {
      await this.fundOperationalStatusService.confirmScheduledInactivation(fundId);
      this.showConfirmInactivationModal = false;
      this.toast.show('info', 'Sucesso', 'Fundo inativado com sucesso.');
      this.onFundOperationalStatusChanged();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.detail || 'Não foi possível confirmar a inativação.'
      );
    } finally {
      this.scheduleActionLoading = false;
    }
  }

  closeFundOperationalModal(): void {
    this.fundOperationalModalMode = null;
    this.fundOperationalPrefill = null;
    this.selectedFundApproval = null;
  }

  onFundOperationalStatusChanged(): void {
    this.closeFundOperationalModal();
    this.loading = true;
    this.getFunds();
  }
}
